export enum UserRole {
  SUPERADMIN = 'SUPERADMIN',
  ADMIN = 'ADMIN',
  USER = 'USER'
}

export enum UserStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE'
}

export type Mode = 'plan' | 'act';

export interface IModelInfo {
  maxTokens?: number;
  contextWindow?: number;
  supportsImages?: boolean;
  supportsPromptCache?: boolean;
  inputPrice?: number;
  outputPrice?: number;
  cacheWritesPrice?: number;
  cacheReadsPrice?: number;
  description?: string;
  temperature?: number;
  isR1FormatRequired?: boolean;
  supportsReasoning?: boolean;
  supportsReasoningEffort?: boolean;
  supportsTools?: boolean;
  supportsStreaming?: boolean;
  systemRole?: string;
}

export interface IApiBaseConfig {
  openAiBaseUrl: string;
  openAiApiKey: string;
  openAiHeaders?: Record<string, string>;
  openAiModelId: string;
  azureApiVersion?: string;
  // Extended model info for OpenAI-compatible providers
  modelInfo?: IModelInfo;
}

export interface IAnthropicConfig {
  apiKey: string;
  anthropicBaseUrl?: string;
  actModeApiModelId?: string;
  planModeApiModelId?: string;
  maxTokens?: number;
  contextWindow?: number;
  supportsImages?: boolean;
  supportsPromptCache?: boolean;
  supportsReasoning?: boolean;
  inputPrice?: number;
  outputPrice?: number;
  cacheWritesPrice?: number;
  cacheReadsPrice?: number;
}

export interface IApiConfig {
  name?: string;
  providerName?: string; // e.g., "OpenAI", "Azure", "OpenAI Compatible", "Anthropic"
  currentMode: Mode;
  planModeApiProvider?: string; // e.g., "openai", "azure", "anthropic"
  actModeApiProvider?: string;
  planModeOpenAiModelId?: string;
  actModeOpenAiModelId?: string;
  planModeOpenAiModelInfo?: IApiBaseConfig;
  actModeOpenAiModelInfo?: IApiBaseConfig;
  anthropic?: IAnthropicConfig;
}

export interface IOrganizationInfo {
  id: string;
  name?: string;
}

export interface ISettings {
  API: IApiConfig[];
  organization?: IOrganizationInfo;
  integrations?: {
    jira?: {
      jiraOrgUrl?: string;
      jiraUsername?: string;
      jiraAccessToken?: string;
    };
    confluence?: {
      confluenceOrgUrl?: string;
      confluenceUsername?: string;
      confluenceAccessToken?: string;
    };
  };
}

export interface ILicense {
  key: string;
  validUntilDate: string;
}

export interface ILicenseHistory {
  key: string;
  validUntilDate: string;
  deactivationDate: string;
}

export interface IUserStatusHistory {
  status: UserStatus;
  changedAt: string;
  changedBy?: string;
  reason?: string;
}

export interface IUser {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  status: UserStatus;
  license: ILicense;
  licenseHistory?: ILicenseHistory[];
  statusHistory?: IUserStatusHistory[];
  settings?: ISettings;
  mfaEnabled?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface IInvoice {
  _id: string;
  userId: string;
  invoiceNumber: string;
  amount: number;
  currency: string;
  description: string;
  issueDate: string;
  dueDate: string;
  paidDate?: string;
  status: 'pending' | 'paid' | 'overdue' | 'cancelled';
  pdfUrl?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface AuthResponse {
  success: boolean;
  token?: string;
  user?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: UserRole;
  };
  mfaRequired?: boolean;
  mfaToken?: string;
  rememberToken?: string;
}

export interface UsageSummary {
  machine: {
    totalInstallations: number;
    activeInstallations: number;
    byOs: { label: string; count: number }[];
    byPlatform: { label: string; count: number }[];
    loginsByDay: { date: string; count: number }[];
    recentInstallations: {
      machineHostname: string;
      machineOs: string;
      vscodePlatform: string;
      extensionVersion: string;
      lastSeenAt: string | Date;
    }[];
  };
  chat: {
    totalTasks: number;
    activeTasks: number;
    deletedTasks: number;
    totalEvents: number;
    totalTokensIn: number;
    totalTokensOut: number;
    totalCost: number;
    averageTokensPerTask: number;
    averageCostPerTask: number;
    tasksByDay: { date: string; count: number }[];
    tokensByDay: { date: string; tokensIn: number; tokensOut: number }[];
    topModels: { label: string; count: number }[];
    topModes: { label: string; count: number }[];
    environmentOs: { label: string; count: number }[];
  };
}
